import React, { useEffect, useRef, useState } from 'react';

const chapters = [
  {
    id: 'rules',
    index: '01',
    eyebrow: 'RULES',
    title: 'Software did what we told it. Mostly.',
    body: 'Input went in. Rules fired. Output came out. If it broke, the bug was probably ours. Comforting, really.',
    notation: 'INPUT  →  RULE  →  OUTPUT  →  BUG',
  },
  {
    id: 'data',
    index: '02',
    eyebrow: 'DATA',
    title: 'Then we fed it examples and hoped.',
    body: 'Instead of explaining every rule, we showed it enough reality to start making suspiciously useful guesses.',
    notation: 'DATA  +  COMPUTE  +  OPTIMISM',
  },
  {
    id: 'models',
    index: '03',
    eyebrow: 'MODELS',
    title: 'We stopped defining every rule. Bold move.',
    body: 'Models built their own representations. They worked remarkably well and explained themselves remarkably badly.',
    notation: 'LOSS ↓  /  CAPABILITY ↑  /  EXPLANATION ?',
  },
  {
    id: 'language',
    index: '04',
    eyebrow: 'LANGUAGE',
    title: 'Language became numbers. Meaning survived.',
    body: 'Words became tokens, ideas became coordinates, and meaning somehow squeezed through the matrix multiplication.',
    notation: 'TOKENS IN  /  MEANING OUT  /  SOMEHOW',
  },
  {
    id: 'generation',
    index: '05',
    eyebrow: 'GENERATION',
    title: 'Autocomplete escaped the textbox.',
    body: 'Predicting the next tiny fragment turned into essays, images, voices, code and an entirely reasonable amount of existential confusion.',
    notation: 'NEXT TOKEN  →  UNREASONABLE CONSEQUENCES',
    control: true,
  },
  {
    id: 'tools',
    index: '06',
    eyebrow: 'TOOLS',
    title: 'Then we gave it tools. Naturally.',
    body: 'Now models can search, calculate, see, write, verify and press buttons in other software. Supervision remains fashionable.',
    notation: 'MODEL  +  TOOLS  +  PERMISSION',
  },
  {
    id: 'now',
    index: '07',
    eyebrow: 'NOW',
    title: 'Software joined the group project.',
    body: 'Not independent. Definitely not infallible. But no longer sitting quietly and waiting for the next click.',
    notation: 'HUMAN  ↔  MACHINE  /  IT’S COMPLICATED',
  },
];

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

function Scene({ activeStage, entropy }) {
  const sceneClass = (index) => `scene scene-${index}${activeStage === index ? ' is-active' : ''}`;
  const dataPoints = [
    [488, 278], [525, 234], [548, 326], [583, 268], [608, 213], [632, 300],
    [684, 247], [716, 198], [741, 281], [778, 226], [805, 173], [837, 257],
  ];
  const tokenWidths = [82, 102, 74, 118, 88];

  return (
    <div className="visual-stage" aria-hidden="true">
      <div className="visual-caption">
        <span>AI / FIELD STUDY</span>
        <span>{String(activeStage + 1).padStart(2, '0')} — 07</span>
      </div>

      <svg className="story-machine" viewBox="0 0 1000 720" role="presentation">
        <defs>
          <linearGradient id="signalGradient" x1="0" x2="1">
            <stop offset="0" stopColor="#baff43" stopOpacity="0" />
            <stop offset="0.45" stopColor="#baff43" />
            <stop offset="1" stopColor="#8dd8ff" stopOpacity="0" />
          </linearGradient>
          <filter id="softGlow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <pattern id="microGrid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.6" />
          </pattern>
        </defs>

        <rect className="machine-grid" x="330" y="80" width="590" height="540" fill="url(#microGrid)" />
        <path className="machine-frame" d="M330 150V80H400 M850 80H920V150 M920 550V620H850 M400 620H330V550" />
        <path className="signal-line" d="M80 586 C210 586 222 523 332 523 S434 174 538 174 S668 535 920 535" />
        <circle className="signal-pulse" cx="0" cy="0" r="5" filter="url(#softGlow)">
          <animateMotion dur="5s" repeatCount="indefinite" path="M80 586 C210 586 222 523 332 523 S434 174 538 174 S668 535 920 535" />
        </circle>

        <g className={sceneClass(0)}>
          <text className="scene-kicker" x="430" y="150">DETERMINISTIC SYSTEM / ALLEGEDLY</text>
          {Array.from({ length: 24 }).map((_, index) => {
            const column = index % 6;
            const row = Math.floor(index / 6);
            const isSignal = [1, 8, 15, 22].includes(index);
            return (
              <g key={index}>
                <rect className={`rule-cell${isSignal ? ' is-signal' : ''}`} x={432 + column * 66} y={205 + row * 66} width="42" height="42" />
                <text className="cell-value" x={453 + column * 66} y={231 + row * 66}>{isSignal ? '1' : '0'}</text>
              </g>
            );
          })}
          <text className="scene-label" x="430" y="520">IF THIS</text>
          <path className="scene-arrow" d="M520 514H690" />
          <text className="scene-label signal-text" x="716" y="520">THEN THAT</text>
        </g>

        <g className={sceneClass(1)}>
          <text className="scene-kicker" x="430" y="150">PATTERN / CONFIDENCE 0.94-ISH</text>
          <path className="plot-axis" d="M440 505V190M440 505H865" />
          <path className="decision-boundary" d="M456 472C560 461 592 391 638 334S734 210 850 205" />
          {dataPoints.map(([x, y], index) => (
            <circle key={index} className={`data-point${index > 5 ? ' is-secondary' : ''}`} cx={x} cy={y + (index % 3) * 28} r={index % 4 === 0 ? 8 : 5} />
          ))}
          <text className="axis-label" x="425" y="184">y</text>
          <text className="axis-label" x="870" y="524">x</text>
          <text className="scene-label signal-text" x="704" y="462">PATTERN FOUND</text>
        </g>

        <g className={sceneClass(2)}>
          <text className="scene-kicker" x="430" y="150">REPRESENTATION SPACE / DO NOT PANIC</text>
          <g className="model-column">
            {[230, 300, 370, 440].map((y, i) => <circle key={y} cx="450" cy={y} r={9 + i * 2} />)}
          </g>
          <g className="model-column model-column-middle">
            {[205, 265, 325, 385, 445].map((y, i) => <circle key={y} cx="640" cy={y} r={6 + (i % 2) * 5} />)}
          </g>
          <g className="model-column model-column-end">
            {[260, 350, 440].map((y, i) => <circle key={y} cx="830" cy={y} r={12 - i * 2} />)}
          </g>
          {[230, 300, 370, 440].map((y, i) => <path key={`a-${y}`} className="model-link" d={`M464 ${y} C535 ${y}, 555 ${215 + i * 66}, 626 ${215 + i * 66}`} />)}
          {[205, 265, 325, 385, 445].map((y, i) => <path key={`b-${y}`} className="model-link" d={`M654 ${y} C730 ${y}, 742 ${270 + (i % 3) * 84}, 816 ${270 + (i % 3) * 84}`} />)}
          <text className="axis-label" x="424" y="505">RAW</text>
          <text className="axis-label" x="594" y="505">LATENT</text>
          <text className="axis-label" x="797" y="505">USEFUL</text>
        </g>

        <g className={sceneClass(3)}>
          <text className="scene-kicker" x="430" y="150">CONTEXT WINDOW / 05 TINY TOKENS</text>
          {['LANG', 'UAGE', 'BEC', 'OMES', 'DATA'].map((token, index) => {
            const x = 408 + tokenWidths.slice(0, index).reduce((sum, width) => sum + width + 12, 0);
            return (
              <g className="token" key={token}>
                <rect x={x} y="300" width={tokenWidths[index]} height="58" rx="2" />
                <text x={x + tokenWidths[index] / 2} y="336">{token}</text>
              </g>
            );
          })}
          <path className="attention-path" d="M446 284C494 212 681 212 738 284" />
          <path className="attention-path attention-path-2" d="M544 374C584 438 765 438 821 374" />
          <circle className="attention-node" cx="446" cy="284" r="4" />
          <circle className="attention-node" cx="738" cy="284" r="4" />
          <text className="scene-label signal-text" x="578" y="498">MEANING HAS GEOMETRY</text>
        </g>

        <g
          className={sceneClass(4)}
          style={{
            '--entropy-rotation': `${(entropy - 34) * 0.08}deg`,
            '--entropy-shift-x': `${(entropy - 34) * -0.18}px`,
            '--entropy-shift-y': `${(entropy - 34) * 0.12}px`,
            '--entropy-rotation-b': `${(entropy - 34) * 0.38}deg`,
            '--entropy-rotation-c': `${(entropy - 34) * -0.3}deg`,
            '--entropy-scale': 0.82 + entropy * 0.003,
          }}
        >
          <text className="scene-kicker" x="430" y="150">SAMPLE / TEMPERATURE {(entropy / 50).toFixed(2)}</text>
          <g className="generation-shapes">
            <rect className="gen-shape gen-a" x="452" y="226" width="128" height="214" rx="64" />
            <rect className="gen-shape gen-b" x="572" y="208" width="168" height="168" />
            <path className="gen-shape gen-c" d="M718 303L842 222L859 405L742 458Z" />
            <circle className="gen-shape gen-d" cx="604" cy="420" r="84" />
            <path className="gen-cut" d="M438 402C540 316 697 492 858 286" />
          </g>
          <text className="scene-label" x="430" y="535">ONE INPUT</text>
          <text className="scene-label signal-text" x="755" y="535">MANY FUTURES</text>
        </g>

        <g className={sceneClass(5)}>
          <text className="scene-kicker" x="430" y="150">TOOL LOOP / SUPERVISED, PROBABLY</text>
          <circle className="agent-orbit" cx="655" cy="350" r="170" />
          <circle className="agent-core" cx="655" cy="350" r="82" />
          <text className="agent-core-label" x="655" y="343">MODEL</text>
          <text className="agent-core-status" x="655" y="372">COORDINATING</text>
          {[
            ['SEE', 655, 180], ['RETRIEVE', 825, 350], ['CHECK', 655, 520], ['ACT', 485, 350],
          ].map(([label, x, y], index) => (
            <g className="tool-node" key={label}>
              <circle cx={x} cy={y} r="35" />
              <text x={x} y={Number(y) + 4}>{label}</text>
            </g>
          ))}
          <path className="orbit-arrow" d="M683 181A172 172 0 0 1 822 323" />
          <path className="orbit-arrow" d="M825 378A172 172 0 0 1 684 518" />
        </g>

        <g className={sceneClass(6)}>
          <text className="scene-kicker" x="430" y="150">CURRENT STATE / VERY UNFINISHED</text>
          <circle className="now-ring now-ring-outer" cx="655" cy="350" r="190" />
          <circle className="now-ring now-ring-middle" cx="655" cy="350" r="134" />
          <circle className="now-ring now-ring-inner" cx="655" cy="350" r="68" />
          <path className="now-wave" d="M408 352C458 352 470 273 518 273S574 436 628 436 690 236 743 236 792 352 902 352" />
          <text className="now-word" x="655" y="342">WE ARE</text>
          <text className="now-word now-word-accent" x="655" y="382">HERE</text>
          <text className="scene-label" x="430" y="570">CAPABILITY ↑</text>
          <text className="scene-label signal-text" x="756" y="570">CERTAINTY ?</text>
        </g>
      </svg>

      <div className="machine-readout">
        <span>STATE</span>
        <strong>{chapters[activeStage].eyebrow}</strong>
        <span className="readout-dot" />
      </div>
    </div>
  );
}

export default function AIStoryPage() {
  const pageRef = useRef(null);
  const chapterRefs = useRef([]);
  const [activeStage, setActiveStage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [entropy, setEntropy] = useState(34);

  useEffect(() => {
    let frame;

    const update = () => {
      const scrollRange = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(scrollRange > 0 ? clamp(window.scrollY / scrollRange) : 0);

      const viewportCenter = window.innerHeight * 0.5;
      let nearest = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      chapterRefs.current.forEach((element, index) => {
        if (!element) return;
        const rect = element.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - viewportCenter);
        if (distance < nearestDistance) {
          nearest = index;
          nearestDistance = distance;
        }
      });

      setActiveStage(nearest);
      frame = undefined;
    };

    const queueUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', queueUpdate, { passive: true });
    window.addEventListener('resize', queueUpdate);

    return () => {
      window.removeEventListener('scroll', queueUpdate);
      window.removeEventListener('resize', queueUpdate);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;

    let frame;
    const updatePointer = (event) => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        page.style.setProperty('--pointer-x', `${(event.clientX / window.innerWidth - 0.5) * 14}px`);
        page.style.setProperty('--pointer-y', `${(event.clientY / window.innerHeight - 0.5) * 14}px`);
        frame = undefined;
      });
    };

    window.addEventListener('pointermove', updatePointer, { passive: true });
    return () => {
      window.removeEventListener('pointermove', updatePointer);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <main className="ai-story" ref={pageRef} style={{ '--story-progress-width': `${progress * 100}%` }}>
      <div className="page-noise" aria-hidden="true" />
      <div className="progress-track" aria-hidden="true">
        <span style={{ transform: `scaleY(${progress})` }} />
      </div>

      <header className="opening" aria-labelledby="opening-title">
        <div className="opening-meta reveal-on-load">
          <div className="owner-lockup">
            <strong>Risto Maasing</strong>
            <span>Chief AI Officer · Tallinn</span>
          </div>
          <div className="edition-lockup">
            <span>Personal field study</span>
            <span>AI / 2026</span>
          </div>
        </div>
        <div className="opening-copy">
          <p className="opening-kicker reveal-on-load">Notes from inside the autocomplete explosion</p>
          <h1 id="opening-title" className="reveal-on-load">AI is not<br /><span>a feature.</span></h1>
          <p className="opening-thesis reveal-on-load">
            I’m Risto, a Chief AI Officer and software builder. I spend my days poking at machines that predict text, write code, use tools, invent plausible nonsense—and somehow keep becoming more useful.
          </p>
        </div>
        <div className="scroll-cue reveal-on-load" aria-hidden="true"><span>Follow the signal</span><i /></div>
      </header>

      <section className="story-sequence" aria-label="The evolution of artificial intelligence">
        <Scene activeStage={activeStage} entropy={entropy} />
        <div className="chapter-flow">
          {chapters.map((chapter, index) => (
            <article
              className={`story-chapter${activeStage === index ? ' is-current' : ''}`}
              id={chapter.id}
              key={chapter.id}
              ref={(element) => { chapterRefs.current[index] = element; }}
            >
              <div className="chapter-copy">
                <div className="chapter-heading"><span>{chapter.index}</span><span>{chapter.eyebrow}</span></div>
                <h2>{chapter.title}</h2>
                <p>{chapter.body}</p>
                <code>{chapter.notation}</code>
                {chapter.control && (
                  <label className="entropy-control">
                    <span><b>CONTROL THE ENTROPY</b><output>{entropy}</output></span>
                    <input aria-label="Generation entropy" type="range" min="0" max="100" value={entropy} onChange={(event) => setEntropy(Number(event.target.value))} />
                  </label>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <footer className="closing">
        <p className="closing-index">08 / UNKNOWN</p>
        <h2>We are still figuring out<br />what on earth we built.</h2>
        <div className="closing-line" aria-hidden="true"><span /></div>
        <div className="signature">
          <div><strong>Risto Maasing</strong><span>Chief AI Officer · Tallinn, Estonia</span></div>
          <p>Built by a human.<br />He had suspiciously capable help.</p>
        </div>
        <div className="footer-links">
          <a href="https://knowzilla.eu" target="_blank" rel="noreferrer">Building at Knowzilla ↗</a>
          <a href="https://github.com/bigr00" target="_blank" rel="noreferrer">GitHub ↗</a>
          <a href="https://linkedin.com/in/ristomaasing" target="_blank" rel="noreferrer">LinkedIn ↗</a>
          <a href="mailto:maasing@gmail.com">Email ↗</a>
        </div>
      </footer>
    </main>
  );
}
